import { FC, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchParams from '@/_constants/SearchParams.ts';
import useCreateCheckoutSession from '@/api/useCreateCheckoutSession.ts';
import { loadStripe } from '@stripe/stripe-js';
import PricingCard from '@/components/ui/PricingCard/PricingCard.tsx';
import { Alert, Tabs } from 'react-daisyui';
import ProductType from '$/_const/ProductType.ts';
import type { ProductType as ProductTypeT } from '$/_const/ProductType.ts';
import useProducts from '@/api/useProducts.ts';
import { useTranslation } from 'react-i18next';

// TODO implement real features per product
const features = ['feature_1', 'feature_2', 'feature_3'];

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);
const PaymentSelectPage: FC = () => {
  const { t } = useTranslation();
  const [selectedPaymentType, setSelectedPaymentType] = useState<ProductTypeT>(ProductType.SUBSCRIPTION);
  const [searchParams] = useSearchParams();
  const redirectUrlParam = searchParams.get(SearchParams.REDIRECT_URL);
  const {
    data: products,
    isLoading: isLoadingProducts,
    error: errorProducts,
    isError: isErrorProducts,
  } = useProducts(true, selectedPaymentType);

  const redirectUrl = redirectUrlParam ? decodeURIComponent(redirectUrlParam) : window.location.href;

  const {
    mutateAsync: createSession,
    isPending: isLoadingCreateSession,
    isError: isErrorCreateSession,
    error: errorCreateSession,
  } = useCreateCheckoutSession();

  const handleCreateCheckoutSession = (priceId: string) => {
    void (async () => {
      try {
        if (isLoadingCreateSession) return;
        const response = await createSession({
          redirectUrl,
          priceId,
        });

        const stripe = await stripePromise;

        return await stripe?.redirectToCheckout({
          sessionId: response.data.sessionId,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <section className="flex">
      <div className="max-w-screen-xl px-4 py-8 mx-auto lg:py-24 lg:px-6">
        <div className="max-w-screen-md flex flex-col gap-6 mx-auto mb-8 text-center lg:mb-12">
          <h2 className="text-3xl font-extrabold tracking-tight">{t('paymentSelectPage.header')}</h2>
          <p className="font-light text-text-secondary sm:text-xl">{t('paymentSelectPage.description')}</p>
          <Tabs variant="boxed">
            <Tabs.Tab
              active={selectedPaymentType === ProductType.SUBSCRIPTION}
              onClick={() => setSelectedPaymentType(ProductType.SUBSCRIPTION)}
            >
              {t('label.subscription')}
            </Tabs.Tab>
            <Tabs.Tab
              active={selectedPaymentType === ProductType.CREDIT_PACKAGE}
              onClick={() => setSelectedPaymentType(ProductType.CREDIT_PACKAGE)}
            >
              {t('label.creditPackage')}
            </Tabs.Tab>
          </Tabs>
          {isErrorProducts && <Alert status="error">{errorProducts.message}</Alert>}
          {isErrorCreateSession && <Alert status="error">{errorCreateSession.message}</Alert>}
        </div>
        {selectedPaymentType === ProductType.SUBSCRIPTION ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
            {isLoadingProducts
              ? Array.from({ length: 3 }).map((_, index) => <PricingCard.Skeleton key={`sub_${index}`} />)
              : products?.map((product) => (
                  <PricingCard.Card
                    key={product.id}
                    title={product.name}
                    price={product.price}
                    pricePeriod={
                      product?.interval_count && product.interval_count > 1
                        ? `${product.interval_count} ${product.interval}s`
                        : product.interval ?? undefined
                    }
                    currency={product.currency}
                    features={features}
                    isLoading={isLoadingCreateSession}
                    onClick={() => handleCreateCheckoutSession(product.price_id)}
                  />
                ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
            {isLoadingProducts
              ? Array.from({ length: 3 }).map((_, index) => <PricingCard.Skeleton key={`package_${index}`} />)
              : products?.map((product) => (
                  <PricingCard.Card
                    key={product.id}
                    title={product.name}
                    price={product.price}
                    features={features}
                    currency={product.currency}
                    isLoading={isLoadingCreateSession}
                    onClick={() => handleCreateCheckoutSession(product.price_id)}
                  />
                ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PaymentSelectPage;
